import { parseOrdinarioLabel } from './recipeVariant.js';
import { mealCompositionForDiet, type CatalogLite } from './collettivaMealTemplates.js';

// T-447 — Resolver UNICO dieta/label → ricetta per la collettiva. Estrae la logica
// finora duplicata in `ProductionPlanPage.autoAssign`, parametrizzata (niente stato
// React / menu di rotazione hard-coded: il piatto di rotazione per categoria arriva
// nel `ctx`). È la SINGOLA fonte di risoluzione letta da Pianifica, DDT ed export.
// Vedi spec §5.1.

/** Ricetta minima per la risoluzione (base o variante). */
export interface ResolverRecipe {
  id: string;
  name: string;
  /** null = ricetta base; valorizzato = variante figlia della base. */
  variant_of_recipe_id: string | null;
  /** codice dieta della variante (null sulle basi). */
  diet_code: string | null;
}

export interface ResolverCtx {
  recipes: ResolverRecipe[];
  /** piatto di rotazione del giorno per categoria PRIMI/SECONDI/CONTORNI (nome ricetta base). */
  rotationByCategory: Record<string, string | undefined>;
  /** etichetta leggibile della dieta dal codice (per il nome nel fallback variant_pending). */
  dietLabel: (code: string) => string;
}

export interface ResolvedDish {
  recipe_id: string;
  recipe_name: string;
  /** dieta risolta della riga (null = ordinaria/base o match diretto). */
  diet_code: string | null;
  /** true = famiglia-ordinario con dieta ma variante figlia non ancora definita
   * → agganciata la base come stima ("da definire" a valle). */
  variant_pending: boolean;
}

const normName = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/**
 * Risolve una label di catalogo (etichetta-dieta o nome piatto) alla ricetta reale.
 * 1) Famiglia ordinario (`parseOrdinarioLabel`): dietCode=null → ricetta di rotazione
 *    base; dietCode valorizzato → ricetta VARIANTE se esiste, altrimenti base +
 *    `variant_pending`. 2) Altrimenti match diretto per NOME sulle ricette base.
 * Ritorna null se non riconosciuto (il chiamante conserva la label come fallback).
 */
export function resolveCollettivaDish(label: string, ctx: ResolverCtx): ResolvedDish | null {
  const bases = ctx.recipes.filter((r) => r.variant_of_recipe_id == null);
  const byName = new Map<string, ResolverRecipe>();
  // Il match per nome usa SOLO le basi: una variante non deve vincere il match per
  // nome (i loro nomi contengono comunque la dieta).
  for (const r of bases) byName.set(normName(r.name), r);

  const parsed = parseOrdinarioLabel(label);
  if (parsed) {
    const dishName = ctx.rotationByCategory[parsed.category];
    if (!dishName) return null;
    const base = byName.get(normName(dishName));
    if (!base) return null;
    if (parsed.dietCode == null) {
      return { recipe_id: base.id, recipe_name: base.name, diet_code: null, variant_pending: false };
    }
    const variant = ctx.recipes.find(
      (r) => r.variant_of_recipe_id === base.id && r.diet_code === parsed.dietCode,
    );
    if (variant) {
      return { recipe_id: variant.id, recipe_name: variant.name, diet_code: parsed.dietCode, variant_pending: false };
    }
    return {
      recipe_id: base.id,
      recipe_name: `${base.name} — ${ctx.dietLabel(parsed.dietCode)}`,
      diet_code: parsed.dietCode,
      variant_pending: true,
    };
  }

  // Non famiglia ordinario → match diretto per nome ricetta base (T-431).
  const direct = byName.get(normName(label));
  if (direct) return { recipe_id: direct.id, recipe_name: direct.name, diet_code: null, variant_pending: false };
  return null;
}

// ————————————————————————————————————————————————————————————————
// Aggregatore ordini-destinazione → piatti reali (spec §5.2, T-447)
// ————————————————————————————————————————————————————————————————

/**
 * Sottoinsieme STRUTTURALE della riga d'ordine collettiva letto dal DDT.
 * `SupplyRequestLine` lo soddisfa (stessi campi) → nessun import del tipo Zod qui,
 * la logica resta pura e testabile. Bed-level = `letto` valorizzato (`qty` = pasti,
 * porta la dieta in `menu_label`/`description` + eventuale trittico del preposto);
 * riga catalogo = `letto` assente (`description` = piatto già nominato).
 */
export interface DishOrderLine {
  meal_service: string;
  description?: string | null;
  qty: number;
  menu_label?: string | null;
  letto?: string | null;
  primo?: { label?: string | null } | null;
  secondo?: { label?: string | null } | null;
  contorno?: { label?: string | null } | null;
  frutta?: { label?: string | null } | null;
}

export interface AggregatedDish {
  recipe_id: string; // '' se il piatto non è risolvibile a una ricetta (fallback)
  recipe_name: string;
  diet_code: string | null;
  meal_service: string;
  qty: number;
  variant_pending: boolean;
}

/** Piatti-label di una riga bed (trittico del preposto se c'è, altrimenti auto-composizione della dieta). */
function bedDishLabels(line: DishOrderLine, catalog: CatalogLite[]): string[] {
  const auto = mealCompositionForDiet(line.menu_label ?? '', catalog);
  const slots = [
    line.primo?.label ?? auto.primo,
    line.secondo?.label ?? auto.secondo,
    line.contorno?.label ?? auto.contorno,
    line.frutta?.label ?? auto.frutta,
  ];
  return slots.filter((l): l is string => !!l && l.trim().length > 0);
}

/**
 * Espande le righe d'ordine di UNA destinazione nei piatti reali e somma le
 * porzioni per `(recipe_id|label, diet_code, meal_service)`. Le righe bed-level si
 * espandono nelle portate (trittico o auto-composizione); le righe catalogo sono già
 * un piatto. Ogni piatto viene risolto alla ricetta reale col resolver condiviso;
 * i piatti non risolvibili sono CONSERVATI (recipe_id vuoto, nome = label) per non
 * perdere mai un pasto (coerente con T-445 Fase 2). Vedi spec §5.2.
 */
export function aggregateDestinationDishes(
  lines: DishOrderLine[],
  ctx: ResolverCtx & { catalog: CatalogLite[] },
): AggregatedDish[] {
  const m = new Map<string, AggregatedDish>();
  const add = (label: string, qty: number, meal: string): void => {
    if (qty <= 0 || !label.trim()) return;
    const resolved = resolveCollettivaDish(label, ctx);
    const recipeId = resolved?.recipe_id ?? '';
    const dietCode = resolved?.diet_code ?? null;
    const key = `${recipeId || normName(label)}::${dietCode ?? ''}::${meal}`;
    const cur = m.get(key);
    if (cur) {
      cur.qty += qty;
      return;
    }
    m.set(key, {
      recipe_id: recipeId,
      recipe_name: resolved?.recipe_name ?? label,
      diet_code: dietCode,
      meal_service: meal,
      qty,
      variant_pending: resolved?.variant_pending ?? false,
    });
  };

  for (const l of lines) {
    const qty = Number(l.qty) || 0;
    if (qty <= 0) continue;
    const meal = l.meal_service;
    if ((l.letto ?? '').trim()) {
      for (const dishLabel of bedDishLabels(l, ctx.catalog)) add(dishLabel, qty, meal);
    } else {
      add((l.description ?? '').trim(), qty, meal);
    }
  }

  return [...m.values()].sort(
    (a, b) => a.meal_service.localeCompare(b.meal_service) || a.recipe_name.localeCompare(b.recipe_name, 'it'),
  );
}
